import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { STRINGS } from "@/lib/i18n";
import { fetchLanguages, fetchUiStrings, adminListLanguages } from "@/lib/api";

const LanguageContext = createContext(null);
export { LanguageContext };
const isCustom = (c) => c && c !== "en" && c !== "hi";

// Dual-tier language model:
//  • globalLang  — the site-wide default (persisted, changed from the header).
//  • overrides   — per-page overrides keyed by pathname (persisted). A page
//                  with an override reads THAT language only; every other page
//                  keeps following the global default. Changing the global
//                  default never touches a page's override.
const OVR_KEY = "dharmartha_lang_overrides";
const readOverrides = () => { try { return JSON.parse(localStorage.getItem(OVR_KEY) || "{}") || {}; } catch { return {}; } };

export const LanguageProvider = ({ children }) => {
  const [globalLang, setGlobalLang] = useState(() => localStorage.getItem("dharmartha_lang") || "en");
  const [overrides, setOverrides] = useState(readOverrides);
  const [path, setPath] = useState(() => (typeof window !== "undefined" ? window.location.pathname : "/"));

  const pageLang = overrides[path] || null;
  const lang = pageLang || globalLang;

  const [languages, setLanguages] = useState([
    { code: "en", label: "English", native: "English" },
    { code: "hi", label: "Hindi", native: "हिन्दी" },
  ]);
  const [uiStrings, setUiStrings] = useState({});

  // Load available languages (public enabled + admin drafts when signed in as admin).
  useEffect(() => {
    fetchLanguages().then((pub) => {
      const token = localStorage.getItem("dharmartha_token");
      if (!token) { setLanguages(pub); return; }
      adminListLanguages()
        .then((all) => {
          const merged = [...pub];
          all.forEach((a) => {
            if (!merged.find((m) => m.code === a.code)) {
              merged.push({ code: a.code, label: a.label, native: a.native_label || a.label, draft: !a.enabled });
            }
          });
          setLanguages(merged);
        })
        .catch(() => setLanguages(pub));
    }).catch(() => {});
  }, []);

  // Load engine UI strings for EVERY non-English language (Hindi included) so
  // the centralized translation layer — not scattered tables — is the source.
  useEffect(() => {
    if (lang && lang !== "en") {
      fetchUiStrings(lang).then(setUiStrings).catch(() => setUiStrings({}));
    } else {
      setUiStrings({});
    }
  }, [lang]);

  // GLOBAL toggle: change the site-wide default language and remember it.
  const changeLang = useCallback((code) => {
    localStorage.setItem("dharmartha_lang", code);
    setGlobalLang(code);
    // No full-page reload — routed pages remount (keyed by language in App.js)
    // so localized data refetches cleanly without a blank/flicker.
  }, []);

  // PER-PAGE toggle: override the language for the CURRENT page only, remembered
  // independently. Passing null (or the current global) clears the override so
  // the page follows the global default again.
  const setPageLang = useCallback((code) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (!code) delete next[path];
      else next[path] = code;
      localStorage.setItem(OVR_KEY, JSON.stringify(next));
      return next;
    });
  }, [path]);

  // Router syncs the active pathname here so per-page overrides resolve correctly.
  const setLangPath = useCallback((p) => setPath(p || "/"), []);

  // Reflect the active language on <html lang> so language-scoped CSS applies.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang || "en");
    }
  }, [lang]);

  const t = useCallback((key) => {
    if (uiStrings[key]) return uiStrings[key];
    const table = STRINGS[lang] || STRINGS.en;
    return table[key] ?? STRINGS.en[key] ?? key;
  }, [lang, uiStrings]);

  // Shared translator for raw English UI text (English text is the key).
  // Returns { text, pending } — pending=true means no translation exists for
  // the active language yet, so the caller shows English WITH a marker.
  const tx = useCallback((text) => {
    if (!text || typeof text !== "string") return { text: text || "", pending: false, lang };
    if (lang === "en") return { text, pending: false, lang };
    const key = text.trim();
    const hit = uiStrings[key] || uiStrings[text];
    if (hit) return { text: hit, pending: false, lang };
    return { text, pending: true, lang };
  }, [lang, uiStrings]);

  // Read a {en, hi} object. Non-Hindi languages read the 'en' slot, which the
  // API overlays with the active language's published translation.
  const L = useCallback((obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    if (lang === "hi") return obj.hi || obj.en || "";
    return obj.en || obj.hi || "";
  }, [lang]);

  // Choose between a Devanagari/native string and the English (localised) slot.
  const pick = useCallback((deva, en) => {
    if (lang === "hi") return deva || en || "";
    return en || deva || "";
  }, [lang]);

  const pickTitle = useCallback((item) => {
    if (!item) return "";
    if (lang === "hi") return item.hindi_title || item.devanagari_title || item.title || "";
    if (lang === "en") return item.english_title || item.title || item.devanagari_title || "";
    return item.title || item.english_title || item.devanagari_title || "";
  }, [lang]);

  const pickSecondary = useCallback((item) => {
    if (!item) return "";
    const dual = item.dual_script ?? ["mantra", "stotra", "sukta"].includes(item.type);
    if (!dual) return "";
    const primary = lang === "hi" ? (item.hindi_title || item.devanagari_title || item.title || "")
      : lang === "en" ? (item.english_title || item.title || item.devanagari_title || "")
      : (item.title || item.english_title || item.devanagari_title || "");
    if (lang === "en") return item.devanagari_title && item.devanagari_title !== primary ? item.devanagari_title : "";
    return item.title && item.title !== primary ? item.title : "";
  }, [lang]);

  const pickDesc = useCallback((item) => {
    if (!item) return "";
    const raw = lang === "hi" ? (item.description_hi || item.description || "") : (item.description || item.description_hi || "");
    return raw.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();
  }, [lang]);

  const isDeva = lang !== "en";

  return (
    <LanguageContext.Provider value={{ lang, globalLang, pageLang, changeLang, setPageLang, setLangPath, t, tx, L, pick, pickTitle, pickSecondary, pickDesc, isDeva, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
